'use client';

// @mui
import { Box, Card, CardContent, Typography, Grid, Button } from '@mui/material';
import { LocationOn as LocationOnIcon } from '@mui/icons-material';
// components
import TextMaxLine from '@/components/minimal/text-max-line';
// next
import { useRouter } from 'next/navigation';
import { Image } from '.';
import { defaultMember } from '@/images';
// services
import { SharedCountryDataDto, SharedIndustryInterestDataDto } from '@/services/';

type MarketplaceCardProps = {
  countries?: SharedCountryDataDto[];
  link?: string;
  profession?: string;
  name?: string;
  image?: string;
  locationArea?: string;
  location?: string;
  description?: string;
  button?: string;
  industryInterests?: SharedIndustryInterestDataDto[];
};

export default function MarketplaceCard({
  countries,
  link,
  profession,
  name,
  image,
  locationArea,
  location,
  description,
  button,
  industryInterests,
}: MarketplaceCardProps) {
  const { push } = useRouter();

  const getCountryNameFromCode = (code: string) => {
    return countries?.filter(({ code2 }) => code2 === code)[0]?.countryName || code;
  };

  return (
    <Box
      onClick={link ? () => push(link) : undefined}
      sx={{ minWidth: '270px', paddingBottom: '10px', '&:hover': { cursor: 'pointer' } }}
      className="marketPlaceCard"
    >
      <TextMaxLine
        line={2}
        sx={{ fontWeight: 'bold', minHeight: '50px' }}
        className="marketPlaceTitle"
      >
        {profession ? <>{profession}</> : <>{'-'}</>}
      </TextMaxLine>
      <Box className="marketPlaceMedia">
        <Image
          alt={name ?? ''}
          onError={function (event: any): void {
            event.target.src = defaultMember;
            event.target.srcset = defaultMember.src;
          }}
          cardClass={'marketPlaceCardImg'}
          imageClass={'marketPlaceCardImg'}
          src={image || defaultMember}
        />
      </Box>
      <Card sx={{ minWidth: '100%' }}>
        <CardContent sx={{ height: '355px' }}>
          <Typography
            sx={{ marginTop: '50px', textAlign: 'center' }}
            className="marketPlaceBioName"
          >
            {name}
          </Typography>
          {countries ? (
            <Box
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                marginTop: '16px',
              }}
            >
              <LocationOnIcon sx={{ color: '#5c5f64' }} className="locationIcon" />
              <Typography sx={{ textAlign: 'center' }} className="marketPlaceBioLocation">
                {locationArea ? (
                  <>
                    {locationArea}
                    {','}{' '}
                  </>
                ) : null}
                {getCountryNameFromCode(location ?? '')}
              </Typography>
            </Box>
          ) : null}

          <Box className="marketPlaceCardDescBox" sx={{ marginTop: '14px', mb: 1 }}>
            <Typography
              sx={{
                minWidth: '100%',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 7,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              className="marketPlaceBioDescription"
            >
              {description}
            </Typography>
          </Box>
          {Boolean(industryInterests?.length) && (
            <>
              <hr />
              <Box mt={2}>
                <Grid container spacing={1} justifyContent="center" alignItems="stretch">
                  {industryInterests?.map((interest, index) => {
                    return (
                      <Grid item key={index}>
                        <Box className="marketPlaceBioExperience">
                          <Typography
                            className="marketPlaceExperienceTitle"
                            sx={{ padding: '5px' }}
                          >
                            {interest.title}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </>
          )}
          <Box>
            {button ? (
              <Button className="marketPlaceBioButtonBox">
                <Typography className="marketPlaceBioButtonTitle">{button}</Typography>
              </Button>
            ) : null}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
